/** FLEET_VEHICLES_REGISTER control: sync the vehicle roster from the fleet provider (equivalent
 *  to `manage.py fleetmgmt sync_vehicles`) via the execute endpoint, and open the vehicles list
 *  (the integration edit dialog's Vehicles tab) per fleet system.
 */
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { fleetVehiclesActions, type TenantCtx } from '../tenantLinks';

interface FleetSystem {
  id: number;
  provider: string;
  enabled: boolean;
  vehicle_count: number;
  vehicles_assigned: number;
}

function parseSystems(detail: Record<string, unknown>): FleetSystem[] {
  return Array.isArray(detail.fleet_systems)
    ? (detail.fleet_systems as unknown[]).map((s) => {
        const o = s as Record<string, unknown>;
        return {
          id: Number(o.id),
          provider: String(o.provider ?? ''),
          enabled: o.enabled === true,
          vehicle_count: Number(o.vehicle_count ?? 0),
          vehicles_assigned: Number(o.vehicles_assigned ?? 0),
        };
      })
    : [];
}

export function FleetVehiclesRegisterControl({
  item,
  topId,
  toCode,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  toCode: string;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const systems = parseSystems(item.detail);
  const ctx: TenantCtx = { id: topId, code: toCode };

  const sync = useMutation({
    mutationFn: () => items.execute(topId, item.id),
    onSuccess: (res) => {
      const run = res.run;
      if (run && run.status === 'error') {
        snackbar.show(run.error_message || 'Vehicle sync failed.', 'error');
      } else {
        snackbar.show(run?.summary || 'Vehicle sync complete.', 'success');
      }
      onChanged();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Vehicle sync failed.', 'error'),
  });

  const openVehicles = (fleetId: number) =>
    navigate(
      `/tour-operators/${topId}/integrations/${encodeURIComponent(`fleet:${fleetId}`)}/vehicles/list`,
    );

  if (systems.length === 0) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">
          Set up the fleet management system first (the &ldquo;Set up fleet system&rdquo; step).
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        {systems.map((s) => (
          <Stack key={s.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2">{s.provider}</Typography>
            {/* The roster itself lives on /vehicles — the count is the way in. */}
            <ValueActions chip actions={fleetVehiclesActions(ctx)}>
              <Chip size="small" variant="outlined" label={`${s.vehicle_count} vehicles`} />
            </ValueActions>
            <Chip
              size="small"
              variant="outlined"
              color={s.vehicles_assigned > 0 ? 'success' : 'default'}
              label={`${s.vehicles_assigned} assigned`}
            />
            <Button size="small" onClick={() => openVehicles(s.id)}>
              Open vehicles list
            </Button>
          </Stack>
        ))}
        {canManage && (
          <Box>
            <Button
              size="small"
              variant="contained"
              onClick={() => sync.mutate()}
              disabled={sync.isPending}
              startIcon={sync.isPending ? <CircularProgress size={14} /> : undefined}
            >
              Sync vehicles
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Pulls the roster from the fleet provider (create / update / disable + auto route
              assignment).
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
